import { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { isEmpty, isNil, toNumber, toString } from 'lodash';
import queryString from 'query-string';
import { useLocation, useParams } from 'react-router-dom';
import { CompanyMeasurementDateName } from 'api';
import { firmsAction } from 'common/actions';
import { MEASUREMENT_DATE_FILTER, MEASUREMENT_DATE_TITLE } from 'common/constants/pageFilters';
import { useStore } from 'common/store';
import { SetPageFilters, UseStoreValues } from 'common/types/store';
import { LayoutContext } from 'context';
import { useDateQueryParam } from 'hooks';
import { FIRM_SUMMARY_MOST_RECENT_MEASUREMENT_DATE } from 'pages/Firms/common/constants';
import { useGetMeasurementDatesByCompany } from 'services/hooks/companies';
import { useGetMeasurementDatesByFirm, useRefreshFirmMeasurementDates } from 'services/hooks/firms';
import { formatMeasurementDates, getArrayValue, getObjectValue, getStringValue } from 'utilities';
import { UpdateMeasurementDateParams } from './useFirmMeasurementDateFilter.types';

const useFirmMeasurementDateFilter = () => {
  const [storeValue, dispatch] = useStore() as UseStoreValues;
  const { firmInfo: currentFirm, companyInfo } = getObjectValue(storeValue);
  const { id: currentFirmId } = getObjectValue(currentFirm);
  const { slug: currentFirmSlug } = getObjectValue(currentFirm);
  const { id: currentCompanyId, slug: currentCompanySlug } = getObjectValue(companyInfo);

  const { companySlugParam, firmSlugParam } = useParams<{ companySlugParam: string; firmSlugParam: string }>();

  const location = useLocation();
  const parsedQuery = queryString.parse(location.search);
  const { date: dateQueryParam } = parsedQuery;

  const [selectedMeasurementDate, setSelectedMeasurementDate] = useState<CompanyMeasurementDateName | null>(null);

  const { setCurrentMeasurementDate, setPageFilters } = useContext(LayoutContext);

  const { setDateQueryParam } = useDateQueryParam();

  const {
    data: measurementDates,
    isFetching: isFetchingFirmMeasurementDates,
    isLoading: isLoadingFirmMeasurementDates,
  } = useGetMeasurementDatesByFirm({
    firmId: currentFirmId,
    shouldQueryAutomatically: !isNil(currentFirmId),
  });

  const {
    data: companyDates,
    isFetching: isFetchingCompanyMeasurementDates,
    isLoading: isLoadingCompanyMeasurementDates,
  } = useGetMeasurementDatesByCompany({
    companyId: currentCompanyId,
    shouldQueryAutomatically: !isNil(currentCompanyId),
  });
  const { refreshFirmMeasurementDates } = useRefreshFirmMeasurementDates();

  // Route/store mismatches mean the chip would show the previous workspace's MD; skeleton until context catches up.
  const isFirmContextHydrating = !isNil(firmSlugParam) && currentFirmSlug !== firmSlugParam;
  const isCompanyContextHydrating =
    !isNil(companySlugParam) && (isNil(currentCompanyId) || currentCompanySlug !== companySlugParam);
  const isLoadingMeasurementDates =
    isFirmContextHydrating ||
    isCompanyContextHydrating ||
    (!isNil(companySlugParam)
      ? isLoadingCompanyMeasurementDates || isFetchingCompanyMeasurementDates
      : isLoadingFirmMeasurementDates || isFetchingFirmMeasurementDates);

  const companyMeasurementDates = useMemo<CompanyMeasurementDateName[]>(() => {
    let dates: CompanyMeasurementDateName[];
    if (!isNil(companySlugParam)) {
      // On a company page, only use company-specific dates (id = measurement date ID).
      // Do NOT fall back to firm-level dates — those carry id = CMD ID, which would
      // be passed downstream as the measurement date ID and cause 400 errors on
      // endpoints that expect ?mp=<measurement_date_id>.
      // During the loading window, return [] so nothing is selected; the chip's
      // isLoadingMeasurementDates guard prevents the empty list from being shown.
      dates = !isEmpty(companyDates) ? (companyDates as unknown as CompanyMeasurementDateName[]) : [];
    } else {
      dates = measurementDates ?? [];
    }
    return [FIRM_SUMMARY_MOST_RECENT_MEASUREMENT_DATE, ...getArrayValue(dates)];
  }, [companySlugParam, companyDates, measurementDates]);

  const isMostRecentMeasurementDate = useMemo<boolean>(
    () => toNumber(selectedMeasurementDate?.id) === FIRM_SUMMARY_MOST_RECENT_MEASUREMENT_DATE.id,
    [selectedMeasurementDate]
  );

  const mostRecentMeasurementDate = useMemo<CompanyMeasurementDateName>(() => {
    const dates = !isNil(companySlugParam) ? companyDates : measurementDates;
    return (dates?.[0] as CompanyMeasurementDateName | undefined) ?? FIRM_SUMMARY_MOST_RECENT_MEASUREMENT_DATE;
  }, [companySlugParam, companyDates, measurementDates]);

  const updateMeasurementDate = useCallback(
    (params: UpdateMeasurementDateParams) => {
      const { measurementDateId, measurementDateSlug } = params;
      const measurementDate = getArrayValue(companyMeasurementDates).find(
        companyMeasurementDate =>
          toString(companyMeasurementDate?.id) === measurementDateId ||
          toString(companyMeasurementDate?.slug) === measurementDateSlug ||
          toString(companyMeasurementDate?.date) === measurementDateSlug
      );

      if (!isNil(measurementDate)) {
        // Write the plain calendar date (not the company-specific slug) so the
        // URL param is stable when switching between company and firm contexts.
        // An empty slug signals "Most Recent" — clear the param in that case.
        setDateQueryParam({
          date: measurementDate.slug ? getStringValue(measurementDate.date) : '',
        });

        setCurrentMeasurementDate?.(measurementDate);
        setSelectedMeasurementDate(measurementDate);
      }
    },
    [companyMeasurementDates, setCurrentMeasurementDate, setDateQueryParam]
  );

  const handleMeasurementDateChange = useCallback(
    (event: React.ChangeEvent<HTMLSelectElement>) => {
      const measurementDateId = toString(event.target.value);

      if (measurementDateId) {
        updateMeasurementDate({ measurementDateId });
      }
    },
    [updateMeasurementDate]
  );

  const measurementDateFilter = useMemo<SetPageFilters | null>(() => {
    if (
      isNil(selectedMeasurementDate) || // No measurement date selected
      isEmpty(measurementDates) // There are no measurement dates available
    ) {
      return null;
    }

    return {
      id: MEASUREMENT_DATE_FILTER,
      title: MEASUREMENT_DATE_TITLE,
      options: formatMeasurementDates(companyMeasurementDates ?? []),
      // Emit the resolved real date ID, not the MAX_SAFE_INT sentinel used for "Most Recent".
      selectedValue: selectedMeasurementDate.slug ? selectedMeasurementDate.id : mostRecentMeasurementDate?.id,
      handler: handleMeasurementDateChange,
    } as SetPageFilters;
  }, [
    companyMeasurementDates,
    handleMeasurementDateChange,
    measurementDates,
    mostRecentMeasurementDate?.id,
    selectedMeasurementDate,
  ]);

  const hasNoMeasurementDates = useMemo<boolean>(
    () => !isNil(measurementDates) && isEmpty(measurementDates),
    [measurementDates]
  );

  const isLoadingMeasurementDate = useMemo<boolean>(() => isNil(measurementDateFilter), [measurementDateFilter]);

  // Sync company dates to the global store so consumers don't need to fetch independently.
  // Null when no company is active; the date array (possibly empty while loading) when one is.
  useEffect(() => {
    dispatch(firmsAction.setCompanyMeasurementDateList(isNil(currentCompanyId) ? null : (companyDates ?? null)));
  }, [currentCompanyId, companyDates, dispatch]);

  // Resolve the selected measurement date and write intent to the global store.
  // "Most Recent" stores null so consumers and CompanyChip know not to pin a specific date.
  // A specific date resolves to the company-specific entry (matched by calendar date).
  useEffect(() => {
    const isMostRecent = !selectedMeasurementDate?.slug;

    if (isMostRecent) {
      dispatch(firmsAction.setCurrentMeasurementDate(null));
      return;
    }

    if (isNil(currentCompanyId)) {
      dispatch(firmsAction.setCurrentMeasurementDate(selectedMeasurementDate));
      return;
    }

    if (isEmpty(companyDates)) {
      return;
    }

    const companyDate = companyDates?.find(d => d.date === selectedMeasurementDate?.date);

    if (!isNil(companyDate)) {
      dispatch(firmsAction.setCurrentMeasurementDate(companyDate));
    }
  }, [currentCompanyId, companyDates, selectedMeasurementDate, dispatch]);

  // Set selected measurement date when date query string changes.
  // The URL param may be a full slug (2025-12-31-70073) from chip selection,
  // or a plain date (2025-12-31) from cross-company navigation — accept both.
  // When the param does not exist on the current company/firm list after load,
  // clear it so selection falls back to Most Recent (latest available MD).
  useEffect(() => {
    if (isNil(dateQueryParam)) {
      setSelectedMeasurementDate(FIRM_SUMMARY_MOST_RECENT_MEASUREMENT_DATE);
      return;
    }

    if (dateQueryParam === selectedMeasurementDate?.slug || dateQueryParam === selectedMeasurementDate?.date) {
      return;
    }

    if (isLoadingMeasurementDates) {
      return;
    }

    const dateQuery = toString(dateQueryParam);
    const matchedMeasurementDate = getArrayValue(companyMeasurementDates).find(
      companyMeasurementDate =>
        toString(companyMeasurementDate?.slug) === dateQuery || toString(companyMeasurementDate?.date) === dateQuery
    );

    if (!isNil(matchedMeasurementDate) && matchedMeasurementDate.slug) {
      updateMeasurementDate({ measurementDateSlug: dateQuery });
      return;
    }

    setDateQueryParam({ date: '' });
  }, [
    companyMeasurementDates,
    dateQueryParam,
    isLoadingMeasurementDates,
    selectedMeasurementDate?.date,
    selectedMeasurementDate?.slug,
    setDateQueryParam,
    updateMeasurementDate,
  ]);

  // Set Page Filters
  useEffect(() => {
    if (!isNil(measurementDateFilter)) {
      setPageFilters?.([measurementDateFilter]);
    }

    return () => {
      setPageFilters?.(null);
    };
  }, [measurementDateFilter, setPageFilters]);

  return {
    hasNoMeasurementDates,
    isLoadingMeasurementDate,
    isLoadingMeasurementDates,
    isMostRecentMeasurementDate,
    mostRecentMeasurementDate,
    selectedMeasurementDate,
    companyMeasurementDates,
    updateMeasurementDate,
    refreshFirmMeasurementDates,
  };
};

export default useFirmMeasurementDateFilter;
